import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import KeyIcon from '@mui/icons-material/Key';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Checks if test or demo mode is active in the environment
 */
export const isTestModeActive = () => {
    const testMode = String(process.env.REACT_APP_TEST_MODE || '').toLowerCase().trim();
    return testMode === 'true' || testMode === '1' || testMode === 'yes';
};

export const isDemoModeActive = () => {
    const demoMode = String(process.env.REACT_APP_DEMO || '').toLowerCase().trim();
    return demoMode === 'true' || demoMode === '1' || demoMode === 'yes';
};

/**
 * DemoBar Component
 * Displayed at the top of the Login screen when REACT_APP_DEMO='true' or REACT_APP_TEST_MODE='true'
 * to inform users/testers about the environment and login capabilities.
 */
const DemoBar = ({ onFillCredentials }) => {
    const isDemo = isDemoModeActive();
    const isTest = isTestModeActive();

    if (!isDemo && !isTest) {
        return null;
    }

    const badgeLabel = isDemo ? "DEMO MODE (OFFLINE)" : "TEST MODE (BACKEND)";
    const noticeText = isDemo
        ? "Standalone demo mode active with local mock data (REACT_APP_DEMO=\"true\")."
        : "Test environment active: connects to real backend with password bypass (REACT_APP_TEST_MODE=\"true\").";
    const tipText = isDemo
        ? "Demo Login: Any Parent ID (e.g. 1001) & any Password"
        : "Test Login: Valid Parent ID with any Password";

    return (
        <Box
            sx={{
                width: '100%',
                background: isDemo
                    ? 'linear-gradient(90deg, #4C1D95 0%, #6D28D9 50%, #7C3AED 100%)'
                    : 'linear-gradient(90deg, #92400E 0%, #B45309 50%, #D97706 100%)',
                color: '#FFFFFF',
                py: { xs: 1.25, sm: 1 },
                px: { xs: 1.5, sm: 2 },
                boxShadow: isDemo ? '0 2px 10px rgba(109, 40, 217, 0.25)' : '0 2px 10px rgba(180, 83, 9, 0.25)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                position: 'relative',
                zIndex: 1100,
                animation: 'fadeIn 0.5s ease-in-out',
                '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(-10px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                }
            }}
        >
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'center', md: 'space-between' },
                        flexWrap: 'wrap',
                        gap: { xs: 1, sm: 1.5 },
                        textAlign: { xs: 'center', md: 'left' }
                    }}
                >
                    {/* Left: Badge & Core Notice */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}
                    >
                        <Chip
                            icon={<ScienceIcon style={{ color: '#FEF3C7', fontSize: 18 }} />}
                            label={badgeLabel}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.25)',
                                color: '#FEF3C7',
                                fontWeight: 800,
                                fontSize: '11px',
                                letterSpacing: '0.5px',
                                border: '1px solid rgba(254, 243, 199, 0.4)',
                                px: 0.5,
                                height: '24px'
                            }}
                        />

                        <Typography
                            variant="body2"
                            sx={{
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: { xs: '12px', sm: '13px' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#FEF3C7', display: { xs: 'none', sm: 'inline' } }} />
                            <span>{noticeText}</span>
                        </Typography>
                    </Box>

                    {/* Right: User Information & Login Tip */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '20px',
                                px: 1.5,
                                py: 0.4,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                border: '1px solid rgba(255, 255, 255, 0.25)'
                            }}
                        >
                            <KeyIcon sx={{ fontSize: 15, color: '#FEF3C7' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#FEF3C7',
                                    fontWeight: 700,
                                    fontSize: { xs: '11px', sm: '12px' },
                                    letterSpacing: '0.2px'
                                }}
                            >
                                {tipText}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default DemoBar;
